interface PendingChange {
  id: string;
  empresaId: string;
  userId: string;
  tableName: string;
  operation: 'insert' | 'update' | 'delete';
  data: any;
  timestamp: number;
}

const STORAGE_PREFIX = 'offline_';
const PENDING_CHANGES_KEY = 'pending_changes';

export const offlineStorage = {
  // Generate storage key with empresa_id for data separation
  getStorageKey(empresaId: string, tableName: string): string {
    return `${STORAGE_PREFIX}empresa_${empresaId}_${tableName}`;
  },

  // Save data to localStorage with empresa_id key
  saveOffline(empresaId: string, tableName: string, data: any[]): void {
    try {
      const key = this.getStorageKey(empresaId, tableName);
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now(),
        empresaId
      }));
    } catch (error) {
      console.error('Error saving offline data:', error);
    }
  },

  // Load data from localStorage for specific empresa
  loadOffline(empresaId: string, tableName: string): any[] | null {
    try {
      const key = this.getStorageKey(empresaId, tableName);
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      // Verify empresa_id matches
      if (parsed.empresaId !== empresaId) {
        console.warn('Empresa ID mismatch in offline data');
        return null;
      }

      // Check if data is too old (7 days TTL)
      const age = Date.now() - parsed.timestamp;
      if (age > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(key);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.error('Error loading offline data:', error);
      return null;
    }
  },

  // Add pending change to queue
  addPendingChange(change: Omit<PendingChange, 'id' | 'timestamp'>): void {
    try {
      const changes = this.getPendingChanges();
      const newChange: PendingChange = {
        ...change,
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now()
      };
      changes.push(newChange);
      localStorage.setItem(PENDING_CHANGES_KEY, JSON.stringify(changes));
    } catch (error) {
      console.error('Error adding pending change:', error);
    }
  },

  // Get all pending changes
  getPendingChanges(): PendingChange[] {
    try {
      const stored = localStorage.getItem(PENDING_CHANGES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error getting pending changes:', error);
      return [];
    }
  },

  // Get pending changes for specific empresa
  getPendingChangesByEmpresa(empresaId: string): PendingChange[] {
    return this.getPendingChanges().filter(c => c.empresaId === empresaId);
  },

  // Remove pending change from queue
  removePendingChange(changeId: string): void {
    try {
      const changes = this.getPendingChanges();
      const filtered = changes.filter(c => c.id !== changeId);
      localStorage.setItem(PENDING_CHANGES_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error('Error removing pending change:', error);
    }
  },

  // Clear all pending changes for empresa
  clearEmpresaPendingChanges(empresaId: string): void {
    try {
      const changes = this.getPendingChanges();
      const filtered = changes.filter(c => c.empresaId !== empresaId);
      localStorage.setItem(PENDING_CHANGES_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error('Error clearing empresa pending changes:', error);
    }
  },

  // Clear all offline data for empresa
  clearEmpresaData(empresaId: string): void {
    try {
      const keys = Object.keys(localStorage);
      const empresaPrefix = `${STORAGE_PREFIX}empresa_${empresaId}_`;
      keys.forEach(key => {
        if (key.startsWith(empresaPrefix)) {
          localStorage.removeItem(key);
        }
      });
      this.clearEmpresaPendingChanges(empresaId);
    } catch (error) {
      console.error('Error clearing empresa data:', error);
    }
  },

  // Clear all offline data (on logout)
  clearAllOfflineData(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(STORAGE_PREFIX) || key === PENDING_CHANGES_KEY) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing all offline data:', error);
    }
  }
};

export type { PendingChange };
